import {
    ChangeDetectionStrategy,
    Component,
    effect,
    HostBinding,
    input,
    Input,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
    standalone: true,
    selector: 'cpw-quantity-input',
    imports: [ReactiveFormsModule],
    template: `
        @if (label) {
            <label class="cpw-quantity-label">{{ label }}</label>
        }
        <div class="cpw-quantity-row">
            @if (prefix) {
                <span class="cpw-quantity-prefix">{{ prefix }}</span>
            }
            <div class="cpw-quantity-controls">
                <button
                    type="button"
                    class="cpw-quantity-btn cpw-quantity-btn--decrement"
                    [disabled]="disabled()"
                    (click)="decrement()">
                    &minus;
                </button>
                <input
                    class="cpw-quantity-value"
                    type="text"
                    inputmode="numeric"
                    placeholder="—"
                    [formControl]="control"
                    (keydown)="onKeyDown($event)"
                    (blur)="clampValue()" />
                <button
                    type="button"
                    class="cpw-quantity-btn cpw-quantity-btn--increment"
                    [disabled]="disabled()"
                    (click)="increment()">
                    +
                </button>
            </div>
            @if (suffix) {
                <span class="cpw-quantity-suffix">{{ suffix }}</span>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
            }

            .cpw-quantity-label {
                display: block;
                font-size: 0.75rem;
                font-weight: 500;
                margin-bottom: 0.125rem;
                color: var(--pf-primary-font-color, #333);
            }

            .cpw-quantity-row {
                display: flex;
                align-items: center;
                gap: 0.375rem;
            }

            .cpw-quantity-prefix,
            .cpw-quantity-suffix {
                font-size: 0.75rem;
                font-weight: 600;
                color: var(--pf-primary-font-color, #666);
                white-space: nowrap;
            }

            .cpw-quantity-controls {
                display: flex;
                align-items: center;
                flex: 1;
                border: 1px solid var(--ant-border-color-base, #d9d9d9);
                border-radius: 6px;
                overflow: hidden;
                background: #fff;
            }

            .cpw-quantity-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 32px;
                min-height: 32px;
                border: none;
                background: var(--ant-primary-color-outline, #e6f4ff);
                color: var(--ant-primary-color, #1890ff);
                font-size: 1.125rem;
                font-weight: 600;
                cursor: pointer;
                user-select: none;
                -webkit-tap-highlight-color: transparent;
                transition: background 0.15s ease;

                &:active:not(:disabled) {
                    background: var(--ant-primary-color-deprecated-bg, #bae0ff);
                    transform: scale(0.9);
                    transition:
                        transform 80ms ease,
                        background 0.15s ease;
                }

                &:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
            }

            .cpw-quantity-value {
                flex: 1;
                min-width: 0;
                text-align: center;
                border: none;
                outline: none;
                font-size: 1rem;
                font-weight: 600;
                padding: 0.25rem 0.125rem;
                background: transparent;
                color: var(--pf-primary-font-color, #333);
                -moz-appearance: textfield;

                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                &:disabled {
                    opacity: 0.4;
                }
            }

            @media (max-width: 480px) {
                .cpw-quantity-label {
                    font-size: 0.85rem;
                    margin-bottom: 0.25rem;
                }

                .cpw-quantity-row {
                    gap: 0.5rem;
                }

                .cpw-quantity-prefix,
                .cpw-quantity-suffix {
                    font-size: 0.85rem;
                }

                .cpw-quantity-controls {
                    border-radius: 8px;
                }

                .cpw-quantity-btn {
                    min-width: 48px;
                    min-height: 48px;
                    font-size: 1.5rem;
                }

                .cpw-quantity-value {
                    font-size: 1.25rem;
                    padding: 0.5rem 0.25rem;
                }
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwQuantityInputComponent {
    @Input() control!: FormControl<number | null>;
    @Input() label?: string;
    @Input() prefix?: string;
    @Input() suffix?: string;
    @Input() min?: number;
    @Input() max?: number;

    disabled = input<boolean>(false);

    @HostBinding('attr.tabindex') tabindex = -1;

    private lastStepAt = 0;

    constructor() {
        effect(() => {
            if (this.disabled()) {
                this.control?.disable({ emitEvent: false });
            } else {
                this.control?.enable({ emitEvent: false });
            }
        });
    }

    increment() {
        if (this.disabled() || !this.canStep()) return;
        const current = this.control.value ?? 0;
        const next = current + 1;
        if (this.max !== undefined && next > this.max) return;
        this.control.setValue(next);
    }

    decrement() {
        if (this.disabled() || !this.canStep()) return;
        const current = this.control.value ?? 0;
        const next = current - 1;
        if (this.min !== undefined && next < this.min) return;
        this.control.setValue(next);
    }

    onKeyDown(event: KeyboardEvent) {
        if (this.disabled()) return;
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.increment();
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.decrement();
        }
    }

    private canStep(): boolean {
        const now = Date.now();
        if (now - this.lastStepAt < 50) return false;
        this.lastStepAt = now;
        return true;
    }

    clampValue() {
        const raw = this.control.value;
        if (raw === null || raw === undefined) return;
        const num = typeof raw === 'string' ? parseInt(raw, 10) : raw;
        if (isNaN(num)) {
            this.control.setValue(this.min ?? 0);
            return;
        }
        let clamped = num;
        if (this.min !== undefined && clamped < this.min) clamped = this.min;
        if (this.max !== undefined && clamped > this.max) clamped = this.max;
        if (clamped !== raw) {
            this.control.setValue(clamped);
        }
    }

    focusWithoutKeyboard(selectAll: boolean) {
        const input = document.querySelector(
            'cpw-quantity-input .cpw-quantity-value'
        ) as HTMLInputElement;
        if (input) {
            input.focus();
            if (selectAll) input.select();
        }
    }
}
